import {ArkivFilnodeModell, ArkivMappenodeModell, ArkivnodeId, ArkivNodeModell, DokumentVersjonModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {computed, signal, WritableSignal} from "@angular/core";
import {BehaviorSubject, Observable, Subscription} from "rxjs";
import {erIkkeNil, standard} from "felles-ng2/støttefunksjoner/nilsjekk";
import {erMappeNode, sorterArkivnode} from "felles-ng2/modell/arkivModellHjelpere";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {Mimeinfo} from "felles-ng2/støttefunksjoner/mimetyper";
import {erTomListe} from "felles-ng2/støttefunksjoner/lister";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {ArkivMappenodeDLM} from "felles-ng2/tjenester/datalager/ArkivDatatjeneste";
import {sammeIdSom} from "felles-ng2/modell/modellHjelpere";
import {DataOgMeta, erLevertFraServer, mapTilData} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {DokumentInformasjon} from "felles-ng2/tjenester/DokumentInformasjonTjeneste";

const debug = initDebugger('Node(arkiv)');

export interface NodeAvhengigheter {
    dokumentinformasjon(dokumentversjon: DokumentVersjonModell): DokumentInformasjon;

    barn(forelderId: ArkivnodeId): Observable<DataOgMeta<ArkivNodeModell[]>>;
}

export abstract class Node<T extends ArkivNodeModell = ArkivNodeModell> {
    readonly _erMappe: boolean;
    readonly laster = asynkArbeidPågårSignal();
    readonly fokusert = signal(false);
    nivaa: number;

    private readonly nodeSomSignal: WritableSignal<T>;

    constructor(node: T, public forelder: Node) {
        this._erMappe = node.erMappe; //antar at node ikke kan endres seg fra/til mappe, derfor ikke signal/computed
        this.nivaa = standard(forelder?.nivaa, -1) + 1;
        this.nodeSomSignal = signal(node);
    }

    get node() {
        //node må håndteres som signal da node kan oppdateres (via this.oppdater(..) )
        return this.nodeSomSignal();
    }

    erMappe(): this is Mappe {
        return this._erMappe;
    }

    erFil(): this is Fil {
        return !this._erMappe;
    }

    sti(): Node[] {
        return [...this.forelder.sti(), this];
    }

    /** Denne nodens foreldre, med rotnode som første element i listen og denne mappens direkte forelder som siste element i listen */
    foreldre(): Mappe[] {
        return this.sti().slice(0, -1) as Mappe[];
    }

    /** Terminerer eventuelle abonnemang (rxjs) denne node har startet */
    terminer() {
    }

    /** oppdaterer {@link #node} */
    oppdater(oppdatert: T): Node {
        this.nodeSomSignal.set(oppdatert);
        return this;
    }

    /** Kalles for å signalisere endringer i treet */
    protected publiser() {
        this.forelder.publiser();
    }

    protected slippFokus() {
        this.forelder.slippFokus();
    }

    /** Utflatet liste med alle ekspanderte noder, fra denne noden og nedover */
    abstract ekspanderteNoder(): Node[];

    /** Utflatet liste med alle noder, dvs denne noden og alle dens barn, rekursivt */
    public abstract alleNoder(): Node[];
}

export class Mappe<NODE extends Node = Node> extends Node<ArkivMappenodeDLM> {
    readonly lastet = signal(false);
    readonly barn = signal<NODE[]>([]);
    readonly mapper = computed<Mappe[]>(() => this.barn().filter(barn => barn.erMappe()));
    readonly filer = computed<Fil[]>(() => this.barn().filter(barn => barn.erFil()));
    readonly tom = computed(() => this.lastet() && erTomListe(this.barn()));
    readonly ekspandert = signal(false);

    private barnAbonnemang: Subscription | null = null;

    constructor(mappe: ArkivMappenodeDLM, forelder: Node, protected avhengigheter: NodeAvhengigheter) {
        super(mappe, forelder);
    }

    /** Legger til eller oppdaterer angitte barn, beholder eksisterende barn.
     * @return Node-representasjoner av angitte barn */
    leggTilBarn(...barn: ArkivNodeModell[]): NODE[] {
        const eksisterendeUnntattBarn = this.barn()
          .map(node => node.node)
          .filter(arkivnode => !barn.some(sammeIdSom(arkivnode)));
        this.initBarn([...eksisterendeUnntattBarn, ...barn].sort(sorterArkivnode));
        this.publiser();
        return barn.map(arkivnode => this.finnBarn(arkivnode.id)!);
    }

    fokuser() {
        if (this.fokusert()) return;
        this.slippFokus();
        this.fokusert.set(true);
        this.lastBarn();
    }

    /** når node åpnes - start observable - når denne leverer -> insert elementer rett etter node i liste */
    åpne() {
        debug(`åpne ${this.node.navn}`)
        this.ekspandert.set(true);
        this.lastBarn();
    }

    /** når node lukkes - terminer observable knyttet til denne mappen -> fjern tilhørende noder...
     * @param avsluttAbonnemang hvorvidt vi skal slutte å lytte til endringer på denne mappens direkte innhold/barn */
    lukk(avsluttAbonnemang: boolean = true) {
        debug(`lukke ${this.node.navn} (avsluttAbonnemang: ${avsluttAbonnemang})`)
        //på sikt bør vi kanskje la være å lukke abon. når node kolapses - neste åpne() vil føre til datarelasting - unødvendig?
        if (avsluttAbonnemang) this.terminer();
        //vi lukker alltid eventuelle abonnemang barna måtte ha
        this.barn().forEach(barn => barn.terminer());
        this.ekspandert.set(false);
        this.publiser();
    }

    /** Sti til denne mappen, med rotmappen først i listen, og denne noden som siste element */
    override sti(): Mappe[] {
        return super.sti() as Mappe[];
    }

    ekspanderteNoder(): Node[] {
        const ekspanderteBarn = this.ekspandert() ? this.barn().flatMap(barn => barn.ekspanderteNoder()) : [];
        return [this, ...ekspanderteBarn];
    }

    alleNoder(): Node[] {
        return [this, ...this.barn().flatMap(barn => barn.alleNoder())];
    }

    override terminer() {
        this.barnAbonnemang?.unsubscribe();
        this.barnAbonnemang = null;
    }

    /** Laster nodens barn, sørger for at endringer blir publisert vhja kall til {@link #publiser} */
    protected async lastBarn(): Promise<void> {
        if (erIkkeNil(this.barnAbonnemang)) {
            //barn allerede lastet
            this.publiser();
            return;
        }

        const arbeidPågår = this.laster.start();
        this.barnAbonnemang = this.avhengigheter.barn(this.node.id)
          .pipe(arbeidPågår.avsluttVedLeveranseDersom(erLevertFraServer), mapTilData())
          .subscribe(barn => {
              debug(`lastBarn: subscribe mottatt ${barn.length} barn av ${this.node.navn}`, barn);
              this.initBarn(barn)
              this.lastet.set(true);
              this.publiser();
          });

        return arbeidPågår.vedAvsluttet;
    }

    protected initBarn(barn: ArkivNodeModell[]) {
        //oppdater eksisterende, legg til nye
        const oppdaterteNoder = barn.map(oppdatertBarn => {
            const eksisterende = this.finnBarn(oppdatertBarn.id);
            if (erIkkeNil(eksisterende)) {
                debug(`initBarn: oppdaterer barnet ${eksisterende.node.navn}`, eksisterende.node, oppdatertBarn)
                return eksisterende.oppdater(oppdatertBarn);
            } else {
                //nytt barn
                debug(`initBarn: nytt barn ${oppdatertBarn.navn}`, oppdatertBarn)
                return this.somMappeEllerFil(oppdatertBarn);
            }
        });

        //terminer og fjern slettede
        this.barn()
          .filter(gammel => !oppdaterteNoder.some(oppdatert => oppdatert.node.id == gammel.node.id))
          .forEach(slettet => {
              debug(`initBarn: sletter barn ${slettet.node.navn}`, slettet)
              slettet.alleNoder().forEach(node => node.terminer());
          });

        //console.log('# SETTER BARN', oppdaterteNoder);
        this.barn.set(oppdaterteNoder as NODE[]);
    }

    private finnBarn(id: ArkivnodeId) {
        return this.barn().find(barn => id == barn.node.id);
    }

    private somMappeEllerFil(node: ArkivNodeModell): Node {
        return erMappeNode(node) ?
          new Mappe(node, this, this.avhengigheter) :
          new Fil(node as ArkivFilnodeModell, this, this.avhengigheter.dokumentinformasjon((node as ArkivFilnodeModell).dokument))
    }
}

export class Fil extends Node<ArkivFilnodeModell> {

    readonly dokumentInformasjon: DokumentInformasjon;

    constructor(fil: ArkivFilnodeModell, forelder: Mappe, dokumentInformasjon: DokumentInformasjon) {
        super(fil, forelder);
        this.dokumentInformasjon = dokumentInformasjon;
    }

    ekspanderteNoder(): Node[] {
        return [this];
    }

    alleNoder(): Node[] {
        return [this];
    }

    get standardFormatMimeinfo(): Mimeinfo {
        return this.dokumentInformasjon.standardformat.mimeinfo
    }
}

export class Rot<NODE extends Node = Node> extends Mappe<NODE> {

    private readonly utflatetObserverbar = new BehaviorSubject<Node[]>([]);

    constructor(rot: ArkivMappenodeModell, avhengigheter: NodeAvhengigheter) {
        super(rot, null as any as Node, avhengigheter);
        //set barn basert på angitt rot
        this.initBarn(rot.barn);
        this.lastet.set(true);
        //viser alltid Rot som ekspandert
        this.ekspandert.set(true);
        this.publiser();
    }

    /** Leverer utflatet liste med ekspanderte noder til alle som abonnerer på {@link #somUtflatetObserverbar} */
    protected override publiser() {
        this.utflatetObserverbar.next(this.ekspanderteNoder());
    }

    protected override slippFokus() {
        this.alleNoder().forEach(node => node.fokusert.set(false));
    }

    // Rot-node kan ikke lukkes
    override lukk() {
    }

    //Rot-node har allerede lastet barn (via ArkivMappenodeModell angitt i konstruktør)
    //trenger derfor heller ikke å kalle this.publiser()
    protected override async lastBarn() {
    }

    override sti(): Mappe[] {
        return [];
    }

    /** Kaller {@link #terminer} på alle noder */
    override terminer() {
        this.alleNoder()
          //må droppe Rot-noden - for å unngå rekursiv brønn
          .filter(node => node !== this)
          .forEach(node => node.terminer());
    }

    /** Leverer utflatet liste med ekspanderte noder,
     * ny liste leveres hver gang noder ekspanderes / lukkes */
    public somUtflatetObserverbar(): Observable<Node[]> {
        return this.utflatetObserverbar;
    }

}
